import { Center, Loader, Stack } from "@mantine/core";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useGetEvent } from "../../../api/event";
import { useDeleteSession } from "../../../api/session";
import { useGetTagColors } from "../../../api/tag";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { dateOf } from "../utils/sessionTime";
import {
  buildColumns,
  columnKeyOf,
  hourWindow,
  sessionOccursOnDay,
} from "./calendarLayout";
import CalendarEmptyState, { emptyStateCopy } from "./CalendarEmptyState";
import CalendarGrid from "./CalendarGrid";
import { LOCATION_COLORS, NO_LOCATION_KEY } from "./constants";
import DayTabs from "./DayTabs";
import { useNowMinutes } from "./useNowMinutes";

export default function SessionsCalendar({
  sessions,
  isLoading,
  eventId,
  enableActions,
  eventLocations,
  hasActiveFilters,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openedSessionId, setOpenedSessionId] = useState(null);
  const { data: event } = useGetEvent(eventId);
  const { data: tagColors } = useGetTagColors(eventId);
  const { mutate } = useDeleteSession();
  const confirmDelete = useDeleteConfirm("session", mutate);

  const allSessions = useMemo(
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions]
  );

  const eventDates = useMemo(
    () => event?.dates?.map((date) => date.date) ?? [],
    [event]
  );

  // Event days plus any stray days that have sessions scheduled outside them
  const days = useMemo(() => {
    const uniqueDays = new Set(eventDates);
    allSessions.forEach((session) => uniqueDays.add(dateOf(session.starting)));
    return [...uniqueDays].sort();
  }, [eventDates, allSessions]);

  const today = dayjs().format("YYYY-MM-DD");
  const dayParam = searchParams.get("day");
  const selectedDay = days.includes(dayParam)
    ? dayParam
    : days.includes(today)
      ? today
      : days[0];
  const isToday = selectedDay === today;

  const tickingNowMinutes = useNowMinutes(isToday);
  const nowMinutes = isToday ? tickingNowMinutes : null;

  // Stable color per location across all days and filters. Colors come from
  // the full event locations list so they don't shift when a search or
  // location filter narrows the fetched sessions.
  const locationColors = useMemo(() => {
    const namesByKey = new Map();
    (eventLocations ?? []).forEach((location) =>
      namesByKey.set(String(location.id), location.name)
    );
    allSessions.forEach((session) => {
      const key = columnKeyOf(session);
      if (key !== NO_LOCATION_KEY && !namesByKey.has(key)) {
        namesByKey.set(key, session.location.name);
      }
    });

    const colors = new Map();
    [...namesByKey.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .forEach(([key], index) =>
        colors.set(key, LOCATION_COLORS[index % LOCATION_COLORS.length])
      );
    return colors;
  }, [eventLocations, allSessions]);

  const sessionCountByDay = useMemo(() => {
    const counts = new Map();
    days.forEach((day) =>
      counts.set(
        day,
        allSessions.filter((session) => sessionOccursOnDay(session, day)).length
      )
    );
    return counts;
  }, [days, allSessions]);

  const daySessions = useMemo(
    () =>
      allSessions.filter((session) => sessionOccursOnDay(session, selectedDay)),
    [allSessions, selectedDay]
  );

  const columns = useMemo(
    () => buildColumns({ daySessions, locationColors }),
    [daySessions, locationColors]
  );

  const { startHour, endHour } = useMemo(
    () => hourWindow({ daySessions, day: selectedDay, nowMinutes }),
    [daySessions, selectedDay, nowMinutes]
  );

  const handleDayChange = (day) => {
    setOpenedSessionId(null);
    setSearchParams((params) => {
      params.set("day", day);
      return params;
    });
  };

  // Everything a session block needs beyond its own placement
  const sessionProps = {
    openedSessionId,
    enableActions,
    tagColors,
    onToggle: (sessionId) =>
      setOpenedSessionId((current) =>
        current === sessionId ? null : sessionId
      ),
    onClose: () => setOpenedSessionId(null),
    onDelete: (sessionId) => {
      setOpenedSessionId(null);
      confirmDelete({ id: sessionId });
    },
  };

  if (isLoading) {
    return (
      <Center mih={320}>
        <Loader />
      </Center>
    );
  }

  if (days.length === 0) {
    return <CalendarEmptyState {...emptyStateCopy({ hasActiveFilters })} />;
  }

  return (
    <Stack gap="sm">
      <DayTabs
        days={days}
        eventDates={eventDates}
        selectedDay={selectedDay}
        sessionCountByDay={sessionCountByDay}
        onChange={handleDayChange}
      />

      {daySessions.length === 0 ? (
        <CalendarEmptyState
          {...emptyStateCopy({ hasActiveFilters, day: selectedDay })}
        />
      ) : (
        <CalendarGrid
          columns={columns}
          day={selectedDay}
          startHour={startHour}
          endHour={endHour}
          nowMinutes={nowMinutes}
          sessionProps={sessionProps}
        />
      )}
    </Stack>
  );
}
